import { useCallback, useRef, useState } from 'react'
import { getGcloudAll, getLocalAndroidAll } from 'src/@api/dashboard'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'
import { readLocalDeviceUdid, readUserEmail } from 'src/utils/readAndroidLocalContext'

/** INSTALLED_APP 按包名去重，兼容 bundleid / bundleId */
function getAppBundleId(item) {
  return item?.bundleid || item?.bundleId || ''
}

function dedupeInstalledApps(items) {
  const seen = new Set()
  const result = []

  for (const item of items) {
    const key = getAppBundleId(item)
    if (key) {
      if (seen.has(key)) continue
      seen.add(key)
    }
    result.push(item)
  }

  return result
}

/**
 * Android Cloud / Local 分页列表 Hook，设备切换时重置分页并取消旧请求。
 *
 * @param {Object} options
 * @param {'local'|'cloud'} options.source - 数据源：Local 或 Gcloud
 * @param {string} options.category - 接口 category 参数
 * @param {number} options.limit - 每页条数
 * @returns {{ context: Object|null, data: Array, loading: boolean, total: number, loadNext: Function, reload: Function, setDateRange: Function }}
 */
export function useAndroidDevicePagedData({ source, category, limit }) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [context, setContext] = useState(null)
  const scopeRef = useRef(null)
  const revisionRef = useRef(0)
  const pageRef = useRef({ offset: 0, maxNum: false, loading: false })
  /** @type {React.MutableRefObject<{ startTime: number, endTime: number } | null>} 日期筛选区间（毫秒） */
  const dateRangeRef = useRef(null)

  const loadPage = useCallback(
    async (offset, scope = scopeRef.current, revision = revisionRef.current) => {
      if (!scope || pageRef.current.loading || pageRef.current.maxNum) return

      pageRef.current.loading = true
      const { context: scopedContext, signal, isCurrent } = scope
      const request = source === 'local' ? getLocalAndroidAll : getGcloudAll
      const params = { category, offset, limit }

      if (source === 'local') params.deviceUdid = readLocalDeviceUdid(scopedContext)
      if (source === 'cloud' && scopedContext?.physicalDeviceKey) {
        params.deviceUdid = scopedContext.physicalDeviceKey
      }

      // 日期筛选走接口：不筛选时不传，请求与改动前一致
      const range = dateRangeRef.current
      if (range) {
        if (range.startTime != null) params.startTime = range.startTime
        if (range.endTime != null) params.endTime = range.endTime
      }

      try {
        const response = await request(
          {
            username: source === 'local' ? readUserEmail() : scopedContext?.username || '',
            params
          },
          { signal }
        )

        if (!isCurrent() || revision !== revisionRef.current) return

        const contents = response?.data?.contents
        if (contents) {
          setData(old => {
            const merged = offset === 0 ? [...contents] : [...old, ...contents]
            return category === 'INSTALLED_APP' ? dedupeInstalledApps(merged) : merged
          })
          setTotal(response.data.totalCount || 0)
          pageRef.current.offset = offset
          if (contents.length < limit) pageRef.current.maxNum = true
        } else {
          pageRef.current.maxNum = true
        }
        setLoading(false)
      } catch (error) {
        if (isCurrent() && revision === revisionRef.current && error?.code !== 'ERR_CANCELED') {
          console.error(`Error fetching ${category} data:`, error)
          setLoading(false)
        }
      } finally {
        if (isCurrent() && revision === revisionRef.current) pageRef.current.loading = false
      }
    },
    [category, limit, source]
  )

  // 设备切换：重置分页 state 并从第一页重新请求
  useDeviceScopedInit(
    useCallback(
      (_, scope) => {
        scopeRef.current = scope
        revisionRef.current += 1
        dateRangeRef.current = null
        pageRef.current = { offset: 0, maxNum: false, loading: false }
        setData([])
        setLoading(true)
        setTotal(0)
        setContext(scope.context)
        loadPage(0, scope, revisionRef.current)
      },
      [loadPage]
    )
  )

  const loadNext = useCallback(() => {
    const page = pageRef.current
    if (!page.maxNum && !page.loading) loadPage(page.offset + limit)
  }, [limit, loadPage])

  const reload = useCallback(() => {
    const scope = scopeRef.current
    if (!scope) return

    revisionRef.current += 1
    pageRef.current = { offset: 0, maxNum: false, loading: false }
    setData([])
    setLoading(true)
    setTotal(0)
    loadPage(0, scope, revisionRef.current)
  }, [loadPage])

  /**
   * 设置日期筛选区间并重新从第一页拉取（区间由接口过滤，不是本地过滤）。
   * @param {{ startTime: number, endTime: number } | null} range - 毫秒时间戳；null 表示清除筛选
   */
  const setDateRange = useCallback(
    range => {
      dateRangeRef.current = range
      reload()
    },
    [reload]
  )

  return { context, data, loading, total, loadNext, reload, setDateRange }
}
